import React from "react";
import { useTranslation } from "react-i18next";
import Price from "./Price";
import DishImage from "./DishImage";
import { HeartIcon } from "./Icons";
import DiscountBadge from "./DiscountBadge";
import DishTag from "./DishTag";
import { runningDiscount, discountedUnitPrice, cutsPrice } from "../util/discount";

/** One dish in the menu grid: full-width photo, name, one line of description, price and a heart. */
const MenuItemCard = React.memo(function MenuItemCard({
  item,
  handleAddToOrder,
  isFavorite,
  onToggleFavorite,
}) {
  const { t } = useTranslation();
  const { image_url, translations, price } = item;
  const { name, description } = translations || {};
  const tags = (item.productInfo?.features || []).slice(0, 2);
  const discount = runningDiscount(item);
  const newPrice = discount && cutsPrice(discount) ? discountedUnitPrice(price, discount) : null;

  return (
    <div className="flex flex-col gap-1 rounded-lg bg-surface shadow-card overflow-hidden pb-1">
      <button
        type="button"
        onClick={() => handleAddToOrder(item)}
        className="flex flex-col gap-1 text-start"
      >
        <span className="relative h-[130px] w-full block">
          {discount && <DiscountBadge discount={discount} className="absolute top-2 start-2 z-10" />}
          <DishImage fill src={image_url} alt={name || ""} seed={item.menu_item_id ?? name} />
          {tags.length > 0 && (
            <span className="absolute bottom-2 start-2 end-2 z-10 flex flex-wrap gap-1">
              {tags.map((tag) => (
                <DishTag key={tag} className="bg-surface/90 shadow-card">
                  {tag}
                </DishTag>
              ))}
            </span>
          )}
        </span>
        <span className="px-3.5 pt-2 flex flex-col gap-1">
          <span className="text-sm font-bold text-dark line-clamp-2">{name}</span>
          {description && (
            <span className="text-xs text-muted line-clamp-1">{description}</span>
          )}
        </span>
      </button>
      <div className="flex items-center justify-between px-3.5">
        {newPrice != null ? (
          <span className="flex flex-col leading-tight">
            <Price value={price} className="text-xs text-muted line-through" />
            <Price value={newPrice} className="text-base font-bold text-primary" />
          </span>
        ) : (
          <Price value={price} className="text-base font-bold text-primary" />
        )}
        <button
          type="button"
          onClick={() => onToggleFavorite(item.menu_item_id)}
          aria-pressed={isFavorite ? "true" : "false"}
          aria-label={t("Favorites")}
          className={`w-11 h-11 -me-2.5 flex items-center justify-center ${
            isFavorite ? "text-primary" : "text-dark"
          }`}
        >
          <HeartIcon size={22} filled={isFavorite} />
        </button>
      </div>
    </div>
  );
});

export default MenuItemCard;
